const ALLOWED_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'p',
  'a',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'blockquote',
  'code',
  'pre',
  'br',
  'hr',
  'img',
]);

const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'loading']);

// Tags that should never be preserved; remove entirely (do not unwrap children).
const FORBID_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
]);

function sanitizeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;

  // Allow same-page anchors and same-origin relative links.
  if (href.startsWith('#')) return href;

  // Disallow scheme-relative URLs (e.g. //evil.com) which are cross-origin.
  if (href.startsWith('//')) return null;

  if (href.startsWith('/')) return href;

  const lower = href.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('mailto:')) {
    return href;
  }

  // Reject javascript:, data:, vbscript:, etc.
  return null;
}

export function sanitizeRichTextHtml(html: string): string {
  // Defensive: if executed outside a browser context, return empty string rather than unsafe HTML.
  if (typeof DOMParser === 'undefined') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const sanitizeElement = (el: Element) => {
    // Sanitize children first so we can safely unwrap disallowed tags.
    for (const child of Array.from(el.children)) {
      sanitizeElement(child);
    }

    const tag = el.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      if (FORBID_TAGS.has(tag)) {
        el.remove();
        return;
      }

      // Unwrap unknown tags but preserve their (sanitized) contents.
      const parent = el.parentNode;
      if (!parent) {
        el.remove();
        return;
      }

      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
      return;
    }

    // Remove all attributes except an explicit allowlist.
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (!ALLOWED_ATTRS.has(name)) {
        el.removeAttribute(attr.name);
      }
    }

    if (tag === 'a') {
      const href = el.getAttribute('href');
      if (href) {
        const sanitizedHref = sanitizeHref(href);
        if (sanitizedHref) el.setAttribute('href', sanitizedHref);
        else el.removeAttribute('href');
      }

      const target = el.getAttribute('target');
      if (target && target !== '_blank') {
        el.removeAttribute('target');
      }

      if (el.getAttribute('target') === '_blank') {
        // Prevent tabnabbing.
        el.setAttribute('rel', 'noopener noreferrer');
      } else {
        // If rel is present, ensure it only contains safe tokens.
        const rel = el.getAttribute('rel');
        if (rel) {
          const safe = new Set(['noopener', 'noreferrer']);
          const cleaned = rel
            .split(/\s+/)
            .map((t) => t.trim().toLowerCase())
            .filter((t) => safe.has(t))
            .join(' ');
          if (cleaned) el.setAttribute('rel', cleaned);
          else el.removeAttribute('rel');
        }
      }
    }

    if (tag === 'img') {
      const src = el.getAttribute('src');
      if (src) {
        const allowed = src.startsWith('https://api.5h.hellers.com/api/documents/')
          || src.startsWith('http://')
          || src.startsWith('https://');
        if (!allowed) {
          el.removeAttribute('src');
        }
      } else {
        el.remove();
        return;
      }
    }
  };

  for (const child of Array.from(body.children)) {
    sanitizeElement(child);
  }

  return body.innerHTML;
}
